package ui.campaign.detailv2.preview

import androidx.compose.material3.SnackbarDuration
import androidx.compose.runtime.*
import api.CampaignState
import common.frontend.api.canLayerTopAdd
import common.frontend.api.canLayerTopDelete
import common.frontend.api.canLayerTopEdit
import io.Repository
import kotlinx.coroutines.launch
import org.ssoft.common.tools.nowDate
import ssoft.frontend.generated.resources.Res
import ssoft.frontend.generated.resources.err_state_fail_campaign
import tk.mallumo.compose.navigation.navTo_CampaignEditorUI
import tk.mallumo.compose.navigation.viewmodel.NavigationViewModel
import tk.mallumo.compose.navigation.viewmodel.VM
import tk.mallumo.log.logERROR
import tk.mallumo.utils.DataState
import tools.await
import ui.campaign.detailv2.editor.CampaignEditorArgs
import ui.campaign.detailv2.preview.CampaignPreviewScope.Wrapper
import ui.snackBarStateGlobal
import kotlin.time.Clock

@Suppress("UNCHECKED_CAST")
fun <T> dataContentEqualPolicy(): SnapshotMutationPolicy<T> =
    DataContentEqualPolicy as SnapshotMutationPolicy<T>

private object DataContentEqualPolicy : SnapshotMutationPolicy<DataState<*>> {
    override fun equivalent(a: DataState<*>, b: DataState<*>): Boolean {
        return a == b && a::class == b::class && a.entry == b.entry
    }

    override fun toString() = "DataContentEqualPolicy"
}

@Suppress("NOTHING_TO_INLINE")
inline fun <T> mutableDataState2(
    state: DataState<T>? = null,
    policy: SnapshotMutationPolicy<DataState<T>> = dataContentEqualPolicy()
): MutableState<DataState<T>> = mutableStateOf((state ?: DataState.Idle()), policy)

@VM
class CampaignPreviewVM : NavigationViewModel() {

    lateinit var scopeUi: CampaignPreviewScope

    var state by mutableDataState2<Wrapper>()
        private set

    sealed interface Action {
        data object Edit : Action
        data object Delete : Action
        data object Pause : Action
        data object Resume : Action
        data object Stop : Action

        companion object {
            @Composable
            fun rememberAction(vm: CampaignPreviewVM): (Action) -> Unit {
                return remember {
                    { act ->
                        when (act) {
                            Edit -> vm.edit()
                            Delete -> vm.delete()
                            Pause -> vm.pause()
                            Resume -> vm.resume()
                            Stop -> vm.stop()
                        }
                    }
                }
            }
        }
    }


    private fun edit() {
        state.entry?.data?.also { campaign ->
            scopeUi.nav.navTo_CampaignEditorUI(
                CampaignEditorArgs(
                    companyId = scopeUi.args.companyId,
                    campaignId = campaign.id
                )
            )
        }
    }


    private fun pause() {
        stateChange(CampaignState.PAUSED)
    }

    private fun delete() {
        stateChange(CampaignState.DELETED)
    }

    private fun stop() {
        stateChange(CampaignState.STOPPED)
    }

    private fun resume() {
        state.entry?.data?.start?.date?.also {
            if (it <= Clock.nowDate()) stateChange(CampaignState.ACTIVE)
            else stateChange(CampaignState.PUBLISHED)
        }
    }

    private fun stateChange(newState: CampaignState) {
        scope.launch {
            state.entry?.data?.also { campaign ->
                runCatching {
                    Repository.api.campaign.changeState(
                        companyId = scopeUi.args.companyId,
                        campaignId = campaign.id,
                        newState = newState,
                        expectedImpressions = campaign.impressionExpected
                    )
                }.onFailure {
                    logERROR(it.printStackTrace())
                    snackBarStateGlobal.showSnackbar(
                        duration = SnackbarDuration.Long,
                        message = it.message ?: Res.string.err_state_fail_campaign.await()
                    )
                }.onSuccess {
                    scopeUi.nav.up()
                }
            }

        }
    }

    fun init(scopeUi: CampaignPreviewScope) {
        this.scopeUi = scopeUi
        scope.launch {
            runCatching {
                val data = if (scopeUi.args.campaignGroupId < 0) {
                    Repository.api.campaign.single(
                        companyId = scopeUi.args.companyId,
                        campaignId = scopeUi.args.campaignId
                    )
                } else {
                    Repository.api.campaign.singleByGroupId(
                        companyId = scopeUi.args.companyId,
                        campaignGroupId = scopeUi.args.campaignGroupId
                    )
                }
                scopeUi.args.campaignGroupId = data.groupId
                scopeUi.args.campaignId = data.id

                with(scopeUi) {
                    state = DataState.Result(
                        Wrapper(
                            canAdd = login.canLayerTopAdd(args.companyId),
                            canEdit = login.canLayerTopEdit(args.companyId),
                            canDelete = login.canLayerTopDelete(args.companyId),
                            canCreate = login.canLayerTopAdd(args.companyId),
                            data = data
                        )
                    )
                }

            }.onFailure {
                state = DataState.Error(it.message!!)
            }
        }
    }


    override fun onRelease() {

    }


}
